// Right column of the extension's full-tab dashboard — the second panel of the
// two-column layout that mirrors the web app's desktop home (and the two-column
// Send / Shield / Unshield tab screens).
//
// Content matches the web app: the portfolio-allocation donut, plus the last-24h
// recent-activity card in public mode (private mode has no public op history —
// the same rule the mobile-web home applies).
import React from 'react'
import { View } from 'react-native'

import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import useDashboardAssets from '@common/modules/dashboard/hooks/useDashboardAssets'
import useDashboardRecentActivity from '@common/modules/dashboard/hooks/useDashboardRecentActivity'
import { useRailgunOverviewBalance } from '@common/modules/dashboard/hooks/useRailgunOverviewBalance'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import ActivityCard from '@mobile/modules/dashboard/screens/home/components/ActivityCard'
import PortfolioAllocation from '@mobile/modules/dashboard/screens/home/components/PortfolioAllocation'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'

type Props = {
  mode: Mode
  // Mirrors the overview header's hide-balance toggle — the donut's "Total" is
  // the same number, so it masks together with it.
  balanceHidden?: boolean
}

const DashboardSidePanel: React.FC<Props> = ({ mode, balanceHidden = false }) => {
  const isPrivate = mode === ModeEnum.PRIVATE
  const { navigate } = useNavigation()
  const { theme } = useTheme()
  const { portfolio } = useSelectedAccountControllerState()
  const { assets } = useDashboardAssets({ isPrivate })
  const { recentActivity } = useDashboardRecentActivity({ isPrivate })
  const railgunOverview = useRailgunOverviewBalance(mode)

  const totalUsd = isPrivate
    ? railgunOverview.totalRailgunBalanceForDisplay || 0
    : portfolio?.totalBalance || 0
  const isLoading = isPrivate
    ? railgunOverview.isRailgunBalanceFetching && !railgunOverview.hasPriorBalance
    : !portfolio?.isReadyToVisualize

  // The cards paint `homeCardBackground`, which in LIGHT mode is the same white
  // as the dashboard's page surface (`primaryBackground`) — without a border they
  // dissolve into the page. Draw the same hairline the left column's tab card
  // uses instead of restyling the shared mobile-web cards.
  const cardShell = {
    borderWidth: 1,
    borderColor: theme.homeCardBorder as string,
    borderRadius: RADII.lg,
    overflow: 'hidden' as const
  }

  return (
    <View style={{ gap: 16 }}>
      <View style={cardShell}>
        <PortfolioAllocation
          assets={assets}
          totalUsd={totalUsd}
          hidden={balanceHidden}
          loading={isLoading}
        />
      </View>
      {!isPrivate && (
        <View style={cardShell}>
          <ActivityCard
            items={recentActivity}
            onSeeAll={() => navigate(`${WEB_ROUTES.dashboard}?tab=activity`)}
          />
        </View>
      )}
    </View>
  )
}

export default React.memo(DashboardSidePanel)
